/**
 * My Situation — the in-memory session profile (BUILD-SPEC-2 §3).
 *
 * A single store every tile reads defaults from and writes user entries back
 * to, so income is entered once, not retyped in eight tools. Each field records
 * its provenance — typed by the user, extracted from a document, or assumed as a
 * default — per §3.1.
 *
 * Privacy (SPEC §2 principle 8, SPEC-2 §3.2): the profile lives ONLY in memory.
 * It is never written to storage automatically and is cleared on page unload.
 * Continuity across sessions is opt-in and user-held via the portable export in
 * profile/portable.ts. Nothing is ever sent anywhere.
 */
import { z } from "zod";
import { FilingStatus } from "../data/schemas";
import { MAX_INPUT_MAGNITUDE } from "../ui/form";

/** Where a field's value came from (§3.1). */
export type FieldSource = "typed" | "extracted" | "assumed";

/** A single debt: a balance, its annual interest rate, and a name (§3.1). */
export interface Debt {
  /** Display name, e.g. "Visa" or "Car loan". */
  name: string;
  /** Outstanding balance. */
  balance: number;
  /** Annual interest rate as a percentage, e.g. 22.99 for 22.99% APR. */
  ratePct: number;
}

/** The known fields of the session profile. All optional — a profile fills in
 * over time as the user (or the Readout) supplies values. */
export interface SituationValues {
  filingStatus: FilingStatus;
  /** Two-letter state code, e.g. "ca". */
  stateCode: string;
  /**
   * The id of the mandatory residence-based local tax the household pays — a
   * `localAddOns` id such as `md-montgomery` or `in-marion`, not a place name.
   *
   * The field sat here unread from the day it was declared until 2026-09-02,
   * when Maryland's and Indiana's county taxes reached the tiles that need
   * them: an id is state-scoped and unambiguous, where "Washington" is a county
   * in Maryland, a county in Indiana, and a state. Empty for a household in
   * neither, since nowhere else levies a local tax you cannot opt out of.
   */
  county: string;
  householdSize: number;
  /**
   * Ages of household members.
   *
   * Written by nothing, and deliberately: no surface asks for a list of ages,
   * and deriving one from a child count or a household size is the inference
   * this project refuses. It survives because a restored file may carry it and
   * because {@link SituationValues.qualifyingChildren} — the figure the credits
   * actually turn on — is the thing worth asking for.
   */
  ages: number[];
  /**
   * Children who meet the qualifying-child test, as a **count**.
   *
   * The EITC and the Child Tax Credit both turn on this number, four tiles ask
   * for it, and until 2026-09-05 each of them kept it. The Readout Report
   * counted children under 17 out of `ages` instead, which nothing writes — so
   * it sized both credits for a childless household in the same document whose
   * section above drew the poverty line for a household of four.
   *
   * A count rather than ages because a count is what the statutes take and what
   * a person can answer; the age test is a question the tiles ask in their own
   * words and this field does not second-guess.
   */
  qualifyingChildren: number;
  /**
   * Gross annual income — **wages**, as every surface that reads it treats it:
   * Take-Home, the W-4 estimator and the saved Report all hand it to the tax
   * engine as `wages`.
   *
   * Which is why net self-employment profit does not go here. It did, from two
   * tiles, and the Report then charged the employee's 7.65% FICA on money that
   * owes self-employment tax at roughly twice that — understating the total by
   * about 6.5% of the profit, in a document a household keeps.
   */
  annualIncome: number;
  /**
   * Net profit from self-employment (Schedule C), which is a different
   * quantity from wages and taxed under a different rule (§1401 rather than
   * §3101). Kept so the surfaces that ask for it can share it, and named so
   * nothing mistakes it for a paycheck.
   */
  selfEmploymentProfitAnnual: number;
  /**
   * Cash tips reported to the employer for the year — W-2 box 12, code TP.
   *
   * Named for what the box holds rather than for the deduction it feeds, because
   * the two are not the same number: IRC §224 counts only tips received in an
   * occupation the Treasury lists, and box 14b carries the occupation code that
   * says whether they were (with "000" meaning at least some were not). So this
   * is a ceiling on the qualified figure, and the tile that spends it says so.
   */
  qualifiedTipsAnnual: number;
  /** Qualified overtime compensation for the year — W-2 box 12, code TT. The
   *  premium half of time-and-a-half required by FLSA §7, which is exactly what
   *  IRC §225 deducts, so this box needs no such caveat. */
  qualifiedOvertimeAnnual: number;
  /** Annual pre-tax contributions (401k/HSA/etc.). */
  preTaxContributions: number;
  /**
   * Annual contributions to tax-advantaged retirement accounts (401k/IRA).
   *
   * All of them, which is what §25B's Saver's Credit measures — and the reason
   * the §402(g) figure below is a separate key. One key carried both for a
   * while: the optimizer wrote its 401(k) box here and the Saver's Credit tile
   * wrote 401(k) + IRA + 403(b), so whichever tile the reader opened second
   * decided what the number meant.
   */
  retirementContributionsAnnual: number;
  /**
   * A year's work-related care for a child under 13, or another dependent who
   * qualifies under §21(b)(1). The dependent care tool writes it, and the
   * second-income question reads it as what going back to work costs.
   */
  dependentCareExpensesAnnual: number;
  /**
   * A year's unemployment compensation, from a 1099-G's box 1 (SPEC-5 §7.2),
   * the only thing that writes it. The unemployment estimator reads it to show
   * the tax on what the form reports.
   */
  unemploymentCompensationAnnual: number;
  /**
   * The §402(g) elective deferral alone — what goes into a 401(k)/403(b)
   * through payroll, without the IRA beside it.
   *
   * My Plan's retirement step sizes the gap to the elective limit, and an IRA
   * contribution does not close it.
   */
  elective401kAnnual: number;
  /** Full annual employer match available if you contribute enough to capture it. */
  employerMatchAnnual: number;
  /** Annual employer match you are currently capturing. */
  employerMatchCaptured: number;
  /** Debts with their balances and rates (§3.1). Feeds the high-cost-debt step. */
  debts: Debt[];
  /** Essential monthly expenses (the "sleep at night" number). */
  essentialMonthlyExpenses: number;
  /** Total monthly expenses. */
  totalMonthlyExpenses: number;
  /** Liquid savings / cash on hand. */
  liquidSavings: number;
  /**
   * The reader's own health-plan deductible, typed into the EOB Checker.
   *
   * A plan parameter rather than a household figure, and here for the reason
   * every other shared value is: the Readout's plan-math check asks whether a
   * notice's deductible figure exceeds the reader's own, and had no way to know
   * it. Nothing infers this one.
   */
  planDeductible: number;
  /** The reader's own out-of-pocket maximum, the deductible's twin. */
  planOopMax: number;
  /**
   * Four-digit birth year of the filer (SPEC-5 §7.2). Read by the Social
   * Security, Medicare and earnings-test tiles; written by whichever the reader
   * fills in first. A year rather than `ages` because it is one number a person
   * can answer and it does not go stale on a birthday.
   */
  birthYear: number;
}

export type SituationKey = keyof SituationValues;

/**
 * A finite number inside the magnitude every other door already enforces, or
 * nothing.
 *
 * `.catch(undefined)` is the first half: a snapshot arrives from a file the
 * user chose, and one unreadable field should cost that field and not the rest
 * of their situation, so every entry drops on its own rather than failing the
 * object.
 *
 * The clamp is the second half, and it was missing. `MAX_INPUT_MAGNITUDE` is
 * the ceiling that stops a finite-but-absurd figure from overflowing one
 * multiplication later and throwing a `RangeError` out of `Money.from`, which
 * renders a blank page rather than a bad number — and it was enforced on
 * exactly one of the two doors into a tile. A typed field and a deep link go
 * through `parseNonNegative`; a **restored profile does not**, and a restore is
 * the door that carries a whole situation at once rather than one field. With
 * `1e308` in every value, a portable profile file blanked the Downshift tile.
 *
 * Clamped rather than dropped, to match the other door: `?bal=1e308` leaves the
 * reader with a visible, editable figure, and a value that vanished from My
 * Situation on restore would be a field the person has to notice is gone.
 */
/** A finite number pulled inside {@link MAX_INPUT_MAGNITUDE} from either end. */
function clampMagnitude(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(-MAX_INPUT_MAGNITUDE, Math.min(MAX_INPUT_MAGNITUDE, n));
}

const bounded = z.number().finite().transform(clampMagnitude);
const num = bounded.optional().catch(undefined);
const str = z.string().optional().catch(undefined);

/**
 * What a restored snapshot is allowed to contain.
 *
 * `SituationStore.load` used to spread whatever it was handed straight into the
 * store, and both paths that reach it — the portable profile file and the
 * Standing Ledger — accepted any shape at all: the ledger's schema said
 * `values: z.record(z.string(), z.unknown())`, which checks that `values` is an
 * object and nothing else. So a restore was a way into every tile that the
 * catalog's "no tile throws or paints a non-finite value" sweep does not cover,
 * because that sweep drives form inputs and deep links rather than a restored
 * profile. A `NaN` for `annualIncome` reaches the tax engine the same as a
 * typed one.
 *
 * Only the runtime shape is enforced, not the economics. A negative balance or
 * a zero income is a state a person can genuinely be in and the tiles handle
 * it; a string where a number belongs is not, and never came from here.
 */
/**
 * The most rows a restored list may carry.
 *
 * A magnitude ceiling stops one absurd *value*; it does nothing about an absurd
 * *count*, and a file carries a whole list at once. Fifty thousand debts in a
 * profile file took the Debt Freedom tile past eight seconds and twelve
 * thousand DOM nodes before the tab stopped answering — the same freeze the
 * horizon caps exist to prevent, reached through the one door that had no cap
 * on it. A hundred is far past any household's real list and far short of a
 * number that costs anything to render.
 */
export const MAX_PROFILE_ROWS = 100;

/** Keep the first {@link MAX_PROFILE_ROWS} rows of a restored list, or nothing. */
function rows<T extends z.ZodTypeAny>(
  schema: T,
): z.ZodCatch<z.ZodOptional<z.ZodType<z.infer<T>, z.input<T>>>> {
  return schema
    .transform((list: z.infer<T>) => (list as unknown[]).slice(0, MAX_PROFILE_ROWS) as z.infer<T>)
    .optional()
    .catch(undefined) as never;
}

export const SituationValuesSchema = z
  .object({
    filingStatus: FilingStatus.optional().catch(undefined),
    stateCode: str,
    county: str,
    householdSize: num,
    ages: rows(z.array(bounded)),
    qualifyingChildren: num,
    annualIncome: num,
    selfEmploymentProfitAnnual: num,
    qualifiedTipsAnnual: num,
    qualifiedOvertimeAnnual: num,
    preTaxContributions: num,
    retirementContributionsAnnual: num,
    dependentCareExpensesAnnual: num,
    unemploymentCompensationAnnual: num,
    elective401kAnnual: num,
    employerMatchAnnual: num,
    employerMatchCaptured: num,
    debts: rows(
      z.array(
        z.object({
          name: z.string(),
          balance: bounded,
          ratePct: bounded,
        }),
      ),
    ),
    essentialMonthlyExpenses: num,
    totalMonthlyExpenses: num,
    liquidSavings: num,
    /**
     * The reader's own health-plan deductible, typed into the EOB Checker.
     *
     * A plan parameter rather than a household figure, and here for the same
     * reason every other shared value is: the Readout's plan-math check needs
     * a number the reader has already given another surface. Nothing infers it.
     */
    planDeductible: num,
    planOopMax: num,
    birthYear: num,
  })
  .catch({});

const FieldSourceSchema = z.enum(["typed", "extracted", "assumed"]);

export const SituationSnapshotSchema = z
  .object({
    values: SituationValuesSchema,
    sources: z.record(z.string(), FieldSourceSchema).optional().catch({}),
  })
  .catch({ values: {}, sources: {} });

/**
 * Keep what a snapshot got right and drop what it did not, never throwing.
 *
 * A restore that refuses the whole file over one bad field is worse than one
 * that restores the rest: there are no accounts here, so the file is the only
 * copy the person has.
 */
export function sanitizeSnapshot(snapshot: unknown): SituationSnapshot {
  const parsed = SituationSnapshotSchema.parse(snapshot ?? {});
  const given = (snapshot ?? {}) as { values?: unknown; sources?: unknown };
  const accepted = (parsed.values ?? {}) as Record<string, unknown>;
  const acceptedSources = (parsed.sources ?? {}) as Record<string, unknown>;

  // Walk the keys the file gave, not the schema's — a snapshot that survives
  // this should come back out in the order it went in, because a ledger file
  // round-trips bit for bit and its situation is this object.
  const values: Record<string, unknown> = {};
  for (const key of Object.keys((given.values as Record<string, unknown>) ?? {})) {
    if (accepted[key] !== undefined) values[key] = accepted[key];
  }
  const sources: Record<string, unknown> = {};
  for (const key of Object.keys((given.sources as Record<string, unknown>) ?? {})) {
    // A provenance entry for a value that did not survive is provenance for
    // nothing, and would show a field as "extracted" that is not there.
    if (key in values && acceptedSources[key] !== undefined) sources[key] = acceptedSources[key];
  }
  return { values, sources } as SituationSnapshot;
}

/**
 * The same two ceilings, on the write that does not go through the schema.
 *
 * There are **three** doors into the profile, not two. A typed field and a deep
 * link are clamped by `parseNonNegative` before they get here; a restored file
 * is clamped by {@link SituationValuesSchema}. The third is a **document**: the
 * Readout writes every confirmed field straight through `set`, and its only
 * check was `Number.isFinite`. A W-2 whose wage box holds a 300-digit number
 * put `1e300` into `annualIncome`, and from there into every tile that reads
 * its default from the profile — the same overflow, through the one door where
 * the value was never typed by the person it is about.
 *
 * So the ceiling moves to the write itself, which is where all three doors
 * meet, rather than being repeated at each of them. `set` is also how a tile
 * writes a value it *computed* rather than parsed, so this covers a caller that
 * has no parse boundary to go through at all.
 */
function boundValue<T>(value: T): T {
  if (typeof value === "number") return clampMagnitude(value) as T;
  if (!Array.isArray(value)) return value;
  return value.slice(0, MAX_PROFILE_ROWS).map((row) => {
    if (typeof row === "number") return clampMagnitude(row);
    if (row && typeof row === "object" && "balance" in row && "ratePct" in row) {
      const debt = row as Debt;
      return {
        ...debt,
        balance: clampMagnitude(debt.balance),
        ratePct: clampMagnitude(debt.ratePct),
      };
    }
    return row;
  }) as T;
}

/** A serializable snapshot of the profile (used by the portable export). */
export interface SituationSnapshot {
  values: Partial<SituationValues>;
  sources: Partial<Record<SituationKey, FieldSource>>;
}

type Listener = () => void;

/**
 * The session profile store. Holds values plus per-field provenance, notifies
 * subscribers on change, and can snapshot/load for the portable export. It does
 * no persistence of its own.
 */
export class SituationStore {
  private values: Partial<SituationValues> = {};
  private sources: Partial<Record<SituationKey, FieldSource>> = {};
  private readonly listeners = new Set<Listener>();

  get<K extends SituationKey>(key: K): SituationValues[K] | undefined {
    return this.values[key];
  }

  /** Provenance of a field, or undefined when the field is unset. */
  sourceOf(key: SituationKey): FieldSource | undefined {
    return this.sources[key];
  }

  has(key: SituationKey): boolean {
    return this.values[key] !== undefined;
  }

  /** Set a field and record its provenance, notifying subscribers. */
  set<K extends SituationKey>(
    key: K,
    value: SituationValues[K],
    source: FieldSource = "typed",
  ): void {
    this.values[key] = boundValue(value);
    this.sources[key] = source;
    this.emit();
  }

  /** Remove a single field. */
  unset(key: SituationKey): void {
    delete this.values[key];
    delete this.sources[key];
    this.emit();
  }

  /** Every set field, in insertion order. */
  entries(): { key: SituationKey; value: unknown; source: FieldSource }[] {
    return (Object.keys(this.values) as SituationKey[]).map((key) => ({
      key,
      value: this.values[key],
      source: this.sources[key] ?? "typed",
    }));
  }

  /** Clear the entire profile (also called on page unload). */
  clear(): void {
    this.values = {};
    this.sources = {};
    this.emit();
  }

  /** A deep-enough copy for export. */
  snapshot(): SituationSnapshot {
    return {
      values: {
        ...this.values,
        ...(this.values.ages ? { ages: [...this.values.ages] } : {}),
        ...(this.values.debts ? { debts: this.values.debts.map((d) => ({ ...d })) } : {}),
      },
      sources: { ...this.sources },
    };
  }

  /** Replace the profile contents from a snapshot (used by import). */
  /**
   * Replace the profile from a snapshot, keeping only what it got right.
   *
   * This is the boundary every restore crosses — the portable profile file and
   * the Standing Ledger both land here — so the check belongs here rather than
   * in either caller, where it would have to be written twice and could
   * disagree with itself.
   */
  load(snapshot: SituationSnapshot): void {
    const clean = sanitizeSnapshot(snapshot);
    this.values = { ...clean.values };
    this.sources = { ...clean.sources };
    this.emit();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}
